import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Table, Button, Modal, message, Tag, InputNumber, Input, Space, Select } from "antd";
import { ReloadOutlined, DeleteOutlined, PlusOutlined, DollarOutlined, BankOutlined, SettingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { PageHeader, StatCard, T, cardStyle } from "../../../shared/ui/PageShell";
import DateRangeFilter from "../../../shared/ui/DateRangeFilter";
import usePermissions from "../../../hooks/usePermissions";
import useTablePagination from "../../../shared/ui/useTablePagination";

const CompanyExpenses = () => {
  const { t } = useTranslation();
  const { pagination } = useTablePagination();
  const { hasPermission, hasAny } = usePermissions();
  const canManage = hasAny(["company_expenses.add", "company_expenses.manage"]);
  const canCancelExpense = hasAny(["company_expenses.cancel", "company_expenses.manage"]);
  const canViewExpenseTypes = hasAny([
    "company_expenses.expense_types_view",
    "company_expenses.expense_types_add",
    "company_expenses.expense_types_edit",
    "company_expenses.expense_types_delete",
  ]);
  const canAddExpenseTypes = hasPermission("company_expenses.expense_types_add");
  const canEditExpenseTypes = hasPermission("company_expenses.expense_types_edit");
  const canDeleteExpenseTypes = hasPermission("company_expenses.expense_types_delete");
  const [rows, setRows] = useState([]);
  const [activeTotal, setActiveTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null);
  const [range, setRange] = useState([null, null]);
  const mainCode = readCachedSystemConfig()?.currency_code || "IQD";

  const [expenseTypes, setExpenseTypes] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [addAmounts, setAddAmounts] = useState({});
  const [addNote, setAddNote] = useState("");
  const [saving, setSaving] = useState(false);

  const [typesOpen, setTypesOpen] = useState(false);
  const [typesRows, setTypesRows] = useState([]);
  const [newTypeName, setNewTypeName] = useState("");
  const [typesSaving, setTypesSaving] = useState(false);

  const expLabels = useMemo(() => {
    const map = {};
    expenseTypes.forEach((x) => { map[x.code] = x.name; });
    typesRows.forEach((x) => { map[x.code] = x.name; });
    return map;
  }, [expenseTypes, typesRows]);

  const expenseCategoryCodes = useMemo(
    () => (expenseTypes.length ? expenseTypes.map((x) => x.code) : []),
    [expenseTypes]
  );
  const addModalTotal = Object.values(addAmounts).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const loadTypes = useCallback(async (all = false) => {
    try {
      const res = await apiClient.get("/company-expense-types", { params: all ? { all: 1 } : undefined });
      const list = Array.isArray(res.data?.types) ? res.data.types : [];
      if (all) setTypesRows(list);
      else setExpenseTypes(list.filter((r) => r.is_active !== false));
    } catch {
      if (!all) setExpenseTypes([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter) params.type = typeFilter;
      if (range[0]) params.start_date = dayjs(range[0]).format("YYYY-MM-DD");
      if (range[1]) params.end_date = dayjs(range[1]).format("YYYY-MM-DD");
      const res = await apiClient.get("/company-expenses", { params });
      setRows(Array.isArray(res.data?.expenses) ? res.data.expenses : []);
      setActiveTotal(parseFloat(res.data?.active_total || 0));
    } catch (e) {
      message.error(e?.response?.data?.message || t("loadError", "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [typeFilter, range, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTypes(false); }, [loadTypes]);

  const openAdd = () => { setAddAmounts({}); setAddNote(""); setAddOpen(true); };

  const openTypes = async () => {
    setNewTypeName("");
    await loadTypes(true);
    setTypesOpen(true);
  };

  const submitAdd = async () => {
    const expenses = Object.entries(addAmounts)
      .map(([type, amount]) => ({ type, amount: parseFloat(amount) || 0 }))
      .filter((row) => row.amount > 0);
    if (!expenses.length) {
      message.error(t("vendorsPaymentInvalid", "Enter a valid amount"));
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("/company-expenses", { expenses, note: addNote || null });
      message.success(t("companyExpenseAdded", "Company expense added"));
      setAddOpen(false);
      load();
    } catch (e) {
      message.error(e?.response?.data?.message || t("companyExpenseFailed", "Could not add expense"));
    } finally {
      setSaving(false);
    }
  };

  const addType = async () => {
    const name = newTypeName.trim();
    if (!name) {
      message.error(t("expenseTypeNameRequired", "Enter a type name"));
      return;
    }
    setTypesSaving(true);
    try {
      await apiClient.post("/company-expense-types", { name });
      message.success(t("expenseTypeAdded", "Expense type added"));
      setNewTypeName("");
      await loadTypes(true);
      await loadTypes(false);
    } catch (e) {
      message.error(e?.response?.data?.message || t("loadError", "Failed"));
    } finally {
      setTypesSaving(false);
    }
  };

  const renameType = async (row, name) => {
    const next = (name || "").trim();
    if (!next || next === row.name) return;
    try {
      await apiClient.put(`/company-expense-types/${row.id}`, { name: next });
      await loadTypes(true);
      await loadTypes(false);
    } catch (e) {
      message.error(e?.response?.data?.message || t("loadError", "Failed"));
    }
  };

  const deactivateType = (row) => {
    Modal.confirm({
      title: t("expenseTypeRemoveConfirm", "Hide this expense type?"),
      content: t("expenseTypeRemoveHint", "It stays for old records but won’t show when adding new expenses."),
      okText: t("yesDelete", "Yes"),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await apiClient.delete(`/company-expense-types/${row.id}`);
          await loadTypes(true);
          await loadTypes(false);
          message.success(t("expenseTypeRemoved", "Expense type hidden"));
        } catch (e) {
          message.error(e?.response?.data?.message || t("loadError", "Failed"));
        }
      },
    });
  };

  const cancel = (record) => {
    Modal.confirm({
      title: t("companyCancelExpenseConfirm", "Cancel this company expense?"),
      content: t("vendorCancelExpenseHint", "It stays on record as cancelled and is no longer counted."),
      okText: t("vendorCancelExpense", "Cancel expense"),
      okButtonProps: { danger: true },
      cancelText: t("back", "Back"),
      onOk: async () => {
        try {
          await apiClient.post(`/company-expenses/${record.id}/cancel`);
          message.success(t("vendorExpenseCancelled", "Expense cancelled"));
          load();
        } catch (e) {
          message.error(e?.response?.data?.message || t("companyExpenseFailed", "Could not cancel expense"));
        }
      },
    });
  };

  const columns = [
    { title: "#", key: "idx", width: 46, render: (_, __, i) => <span style={{ color: T.muted }}>{i + 1}</span> },
    {
      title: <b>{t("type", "Type")}</b>, dataIndex: "type", key: "type",
      render: (v, r) => (
        <Tag color={r.cancelled_at ? "default" : "orange"}>
          {expLabels[v] || r.type_name || v || "—"}
        </Tag>
      ),
    },
    {
      title: <b>{t("amountPaid", "Amount")}</b>, dataIndex: "amount", key: "amount", align: "right",
      render: (v, r) => r.cancelled_at
        ? <span style={{ color: T.muted, textDecoration: "line-through" }}>{formatCurrency(v, mainCode)}</span>
        : <b style={{ color: "#d97706" }}>{formatCurrency(v, mainCode)}</b>,
    },
    {
      title: <b>{t("note", "Note")}</b>, dataIndex: "note", key: "note",
      render: (v) => <span style={{ color: T.sub }}>{v || "—"}</span>,
    },
    {
      title: <b>{t("date", "Date")}</b>, dataIndex: "created_at", key: "created_at", width: 110,
      render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY") : "—"}</span>,
    },
    {
      title: <b>{t("createdBy", "Added by")}</b>, dataIndex: "created_by_name", key: "created_by_name",
      render: (v) => <span style={{ color: T.sub, fontSize: 12 }}>{v || "—"}</span>,
    },
    {
      title: <b>{t("status", "Status")}</b>, key: "status", width: 170, align: "center",
      render: (_, r) => (r.cancelled_at
        ? <span style={{ fontSize: 11, color: "#e11d48" }}>✕ {t("vendorExpenseCancelledBy", "Cancelled")}{r.cancelled_by_name ? ` · ${r.cancelled_by_name}` : ""}</span>
        : (canCancelExpense
          ? <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 7 }} onClick={() => cancel(r)}>{t("cancel", "Cancel")}</Button>
          : null)),
    },
  ];

  return (
    <div style={{ padding: 24, background: T.bg, minHeight: "100%" }}>
      <PageHeader
        breadcrumb={[t("sidebarSectionFinances", "Finances"), t("companyExpensesTitle", "Company Expenses")]}
        title={t("companyExpensesTitle", "Company Expenses")}
        subtitle={canManage
          ? t("companyExpensesHint", "Company operating costs by type — add, manage types, or cancel a wrong entry")
          : t("companyExpensesViewHint", "View company operating expenses")}
        action={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {canViewExpenseTypes && (
              <Button icon={<SettingOutlined />} onClick={openTypes} style={{ borderRadius: 9 }}>
                {t("expenseTypesManage", "Expense types")}
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 9 }}>{t("currencyTotalsRefresh", "Refresh")}</Button>
            {canManage && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} style={{ borderRadius: 9 }}>{t("vendorAddExpense", "Add expense")}</Button>
            )}
          </div>
        }
      />

      <div className="velan-stats-row">
        <StatCard icon={<DollarOutlined style={{ color: "#d97706" }} />} label={t("batchExpenses", "Expenses")} value={formatCurrency(activeTotal, mainCode)} iconBg="#fef3c7" valueColor="#d97706" />
        <StatCard icon={<BankOutlined style={{ color: "#2563eb" }} />} label={t("count", "Count")} value={rows.length} iconBg="#dbeafe" />
      </div>

      <div style={{ ...cardStyle, marginBottom: 16, padding: "12px 16px", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <Select
          allowClear showSearch optionFilterProp="label"
          placeholder={t("expenseTypeFilter", "Expense type")}
          style={{ minWidth: 200 }}
          value={typeFilter}
          onChange={(v) => setTypeFilter(v ?? null)}
          options={expenseTypes.map((x) => ({ value: x.code, label: x.name }))}
        />
        <DateRangeFilter
          value={range}
          onChange={(d) => setRange(d || [null, null])}
          showTime={false}
        />
      </div>

      <div style={cardStyle}>
        <Table
          size="small"
          loading={loading}
          dataSource={rows.map((r) => ({ ...r, key: r.id }))}
          columns={columns}
          pagination={pagination}
          scroll={{ x: 700 }}
        />
      </div>

      <Modal
        title={t("companyAddExpense", "Add company expense")}
        open={addOpen}
        onOk={submitAdd}
        confirmLoading={saving}
        onCancel={() => setAddOpen(false)}
        okText={t("purchaseOrdersSave", "Save")}
        cancelText={t("cancel", "Cancel")}
        centered
        destroyOnHidden
        width={440}
      >
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 10 }}>
          {t("companyExpenseFillHint", "Fill any types — leave blank to skip.")}
        </div>
        {expenseCategoryCodes.map((type) => (
          <div key={type} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <div style={{ flex: 1, fontWeight: 600, color: T.text }}>{expLabels[type] || type}</div>
            <InputNumber
              min={0}
              style={{ width: 160 }}
              value={addAmounts[type] ?? null}
              placeholder="0"
              onChange={(v) => setAddAmounts((prev) => ({ ...prev, [type]: v ?? 0 }))}
            />
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "space-between", margin: "8px 0 14px", paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
          <span style={{ color: T.muted, fontSize: 12 }}>{t("total", "Total")}</span>
          <b style={{ color: "#d97706" }}>{formatCurrency(addModalTotal, mainCode)}</b>
        </div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>{t("note", "Note")}</div>
        <Input value={addNote} onChange={(e) => setAddNote(e.target.value)} placeholder={t("note", "Note")} />
      </Modal>

      <Modal
        title={t("expenseTypesManage", "Expense types")}
        open={typesOpen}
        onCancel={() => setTypesOpen(false)}
        footer={null}
        centered
        destroyOnHidden
        width={520}
      >
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 12 }}>
          {t("companyExpenseTypesHint", "These names appear when adding company expenses.")}
        </div>
        {canAddExpenseTypes && (
          <Space.Compact style={{ width: "100%", marginBottom: 14 }}>
            <Input
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder={t("expenseTypeName", "New type name")}
              onPressEnter={addType}
            />
            <Button type="primary" loading={typesSaving} onClick={addType} icon={<PlusOutlined />}>
              {t("add", "Add")}
            </Button>
          </Space.Compact>
        )}
        <Table
          size="small"
          pagination={false}
          dataSource={typesRows.filter((r) => r.is_active !== false).map((r) => ({ ...r, key: r.id }))}
          locale={{ emptyText: t("noDataFound", "No data found") }}
          columns={[
            {
              title: <b>{t("name", "Name")}</b>,
              dataIndex: "name",
              render: (v, r) => (
                canEditExpenseTypes ? (
                  <Input
                    defaultValue={v}
                    onBlur={(e) => renameType(r, e.target.value)}
                    onPressEnter={(e) => { e.target.blur(); }}
                  />
                ) : (
                  <span>{v}</span>
                )
              ),
            },
            {
              title: <b>{t("code", "Code")}</b>,
              dataIndex: "code",
              width: 120,
              render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v}</span>,
            },
            canDeleteExpenseTypes && {
              title: "",
              key: "rm",
              width: 80,
              align: "center",
              render: (_, r) => (
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deactivateType(r)}>
                  {t("hide", "Hide")}
                </Button>
              ),
            },
          ].filter(Boolean)}
        />
      </Modal>
    </div>
  );
};

export default CompanyExpenses;
